import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * Ce qu'un valideur a tranché sur une proposition — la **première matérialisation** de la couche
 * d'historique restée `🟡` en D4 (« tracer qui a proposé quoi, ce qui a été validé / rejeté, et
 * quand »).
 *
 * Deux valeurs, pas trois : « pas encore décidé » n'en est pas une. Une proposition sans ligne de
 * décision est en attente, et c'est `Inquiry.status` qui le dit — le seul `status` du modèle (M3).
 * Ajouter `pending` ici en ferait un second, à tenir cohérent avec le premier.
 */
export const VALIDATION_DECISIONS = ['validated', 'rejected'];
export const validationDecisionSchema = z.enum(VALIDATION_DECISIONS);
/**
 * Le motif de renvoi tel qu'il est borné.
 *
 * C'est une consigne de correction lue sur une tablette, pas un rapport : de l'ordre de la
 * poignée de phrases. Le plafond ne borne pas le métier, il borne le copier-coller d'un écran
 * entier dans une colonne que personne ne relira.
 */
export const MAX_REJECTION_REASON_LENGTH = 2000;
/**
 * Valider un contrôle transmis : **acter la décision prise sur la proposition qu'on a lue**, et
 * clore le contrôle.
 *
 * ⚠️ **Acter *et* matérialiser** — valider écrit la décision, puis recopie la proposition en
 * visite, contrôle, anomalies et pièces (M20, M25). La proposition reste intacte. Ce qui ne
 * s'écrit **pas** à ce moment-là, c'est le rapport PDF : il est demandé, et naît en file (D27).
 *
 * ⚠️ `proposalId` est **obligatoire, et c'est le jeton de concurrence optimiste de la paire
 * lecture/écriture.** Une demande porte jusqu'à `MAX_PROPOSALS_PER_INQUIRY` propositions et
 * l'écran n'affiche que la plus récente : sans ce champ, une saisie déposée entre la lecture et le
 * clic serait validée **à la place de celle que le valideur a lue**, sans un mot. Le valideur
 * signerait une fiche qu'il n'a pas vue — et une validation est quasi-irréversible.
 *
 * `strictObject`, comme `assignInquirySchema` : un `status` de trop se rejette au lieu de se
 * retirer en silence — l'appelant croirait avoir imposé la transition.
 */
export const validateInspectionSchema = z.strictObject({
    proposalId: uuidV7Schema,
    /**
     * « Travaux à réaliser » **corrigés au bureau** avant de valider (#345) : ils remplacent ceux de
     * la tablette sur le contrôle et le rapport. Absent : ceux de la tablette ; `null` : aucun.
     */
    recommendations: z.string().nullable().optional(),
});
/**
 * Renvoyer un contrôle au terrain pour correction (M4 : `submitted → in_progress`).
 *
 * ⚠️ **Le prototype validé ne montre aucun geste de renvoi** — aucune occurrence de renvoi, de
 * rejet ni de refus de validation dans ses cinq fichiers. L'action est déclarée par le modèle,
 * pas par le client : son libellé d'écran (« Renvoyer au terrain ») vient de l'issue, et il
 * restera à montrer à GHP.
 */
export const rejectInspectionSchema = z.strictObject({
    proposalId: uuidV7Schema,
    /**
     * ⚠️ **Obligatoire, et `.trim()` avant `.min(1)`.** C'est la seule chose que le renvoi
     * transporte : l'agent le lira sur sa tablette avant de reprendre sa saisie. Un motif blanc
     * passerait la garde et renverrait un contrôle sans dire quoi corriger — l'agent redéposerait la
     * même fiche, et le geste aurait coûté un aller-retour pour rien. `z.string().min(1)` seul
     * accepte `'   '`, d'où l'ordre.
     */
    reason: z.string().trim().min(1).max(MAX_REJECTION_REASON_LENGTH),
});
/**
 * D4 — le corps du `409` des deux décisions, **dans le domaine** et non dans l'API : c'est le
 * back-office qui en dépend pour dire *pourquoi* le geste n'est pas passé, et #111 exige que
 * « l'action ne paraisse pas passée si elle ne l'est pas ».
 *
 * ⚠️ **Un `409` signifie toujours que rien n'a été écrit** — même invariant que
 * `depositConflictSchema`, et ici il est garanti par la transaction, pas par la discipline : le
 * refus est levé *dans* la transaction, qui s'annule en entier.
 *
 * ⚠️ Ces refus sont une **dérogation assumée à M4**, qui dit que l'API applique la transition
 * inattendue et la journalise. La dérogation ne porte pas sur une route qui transporterait un
 * statut cible — celle-là n'existe pas — mais sur deux **gestes métier nommés** dont le sens
 * *est* une transition : « valider » veut dire « acter la décision et clore le contrôle ».
 * Il n'y a pas là de transition inattendue à tolérer, il y a une précondition qui ne tient pas,
 * et trancher quand même voudrait dire clore un contrôle sur une proposition que la demande n'a
 * pas, ou reclore un contrôle déjà clos. M21 nomme d'ailleurs le refus lui-même : « acte irréversible
 * dont la mention manque ». Et chaque refus est **journalisé en plus d'être opposé** — c'est ce
 * journal-là que M4 réclame.
 */
export const validationConflictCodeSchema = z.enum([
    /**
     * La demande n'est pas au point de départ de la marche. Le message **nomme la transition
     * refusée** : c'est le critère d'acceptation de #110.
     */
    'transition_not_suggested',
    /**
     * La mention obligatoire du signataire n'est pas cochée sur la fiche transmise. **Le seul refus
     * que M21 prescrit** (« acte irréversible dont la mention manque » : la signature exige la
     * certification, M20) et le seul que le prototype montre à l'écran.
     *
     * ⚠️ Il ne vaut **que pour la validation**. Renvoyer au terrain n'est ni irréversible ni
     * destructeur : c'est précisément le geste de rattrapage qu'on offre quand la fiche est
     * incomplète, et l'exiger là interdirait de renvoyer les fiches qui en ont le plus besoin.
     */
    'certification_missing',
    /**
     * La proposition désignée n'est plus la plus récente : une saisie est arrivée depuis la lecture.
     *
     * ⚠️ Propre à la validation aussi. Le renvoi ne consomme pas la proposition, il rouvre la
     * demande — une saisie plus récente ne rend pas le renvoi faux, et refuser obligerait le
     * valideur à recharger pour redire la même chose.
     */
    'proposal_superseded',
    /**
     * La proposition désignée ne se relit pas dans cette version de l'application : valider
     * **écrit la fiche** en base (M20), et une fiche qui ne se relit pas ne s'écrit pas.
     *
     * ⚠️ **Né avec la matérialisation, le 21/09/2026.** Avant, valider ne lisait que la mention du
     * signataire et passait sur une charge utile illisible sans rien en tirer ; désormais le refus
     * est structurel — l'une des trois raisons de refuser que M21 admet. L'écran ne peut pas le
     * provoquer en temps normal (il n'affiche que les saisies lisibles) : c'est le garde d'un appel
     * direct, et la seule sortie est le renvoi au terrain.
     */
    'proposal_unreadable',
]);
export const validationConflictSchema = z.object({
    code: validationConflictCodeSchema,
    message: z.string(),
});
/**
 * La fiche transmise porte-t-elle la **mention obligatoire du signataire** — « le signataire
 * certifie nous avoir montré et rendu accessible tous les points d'eau du bâtiment », verbatim du
 * prototype validé, qui l'annote « * — obligatoire pour valider » ?
 *
 * C'est la précondition que M21 range en « acte irréversible dont la mention manque » : un tiers
 * ne peut pas attester de ce qu'on ne lui a pas énoncé, et une validation est quasi-irréversible.
 * Elle vit **côté serveur** parce que le prototype est lui-même incohérent — son bouton de
 * l'onglet Signature pose la garde, celui de l'en-tête ne la pose pas. Si elle n'était que dans
 * l'écran, elle n'existerait sur aucun des deux chemins.
 *
 * ⚠️ Lue dans la charge utile **brute**, sans conversion de version — même technique que
 * `proposalReferencesMedium`, pour la raison **inverse** : là il s'agit de ne rien perdre d'un
 * relevé par ailleurs illisible, ici de ne rien laisser passer. La question « le signataire a-t-il
 * certifié ? » doit rester répondable sur n'importe quelle version stockée, présente ou future, et
 * l'absence de réponse **ferme** le geste au lieu de l'ouvrir. Rien d'autre que `true` ne compte
 * pour une mention cochée.
 */
export function proposalCertifiesWaterPoints(payload) {
    if (typeof payload !== 'object' || payload === null)
        return false;
    return payload.certifiedWaterPoints === true;
}
//# sourceMappingURL=decision.js.map